const API_URL = "https://api.judge.me/api/v1/widgets/product_review";
const FEATURED_API_URL =
  "https://api.judge.me/api/v1/widgets/featured_carousel";
const REVIEW_DETAIL_API_URL = "https://api.judge.me/reviews/reviews_for_widget";
const FEATURED_CANDIDATE_LIMIT = 25;
const FEATURED_CONCURRENCY = 3;
const FEATURED_TIMEOUT = 8000;

function safeMediaUrl(value, origin = window.location.origin) {
  if (!value) return null;
  try {
    const normalizedValue = value.startsWith("//") ? `https:${value}` : value;
    const url = new URL(normalizedValue, origin);
    return url.protocol === "https:" ? url.href : null;
  } catch (_error) {
    return null;
  }
}

/**
 * Keep manual items within the limit and fill their unreserved slots with API items.
 * @template T
 * @param {{ item: T, position: number }[]} manual
 * @param {T[]} api
 * @param {number} limit
 * @returns {T[]}
 */
function blendReviewItems(manual, api, limit) {
  const selected = manual.slice(0, limit);
  const slots = [];
  for (const { item } of selected.filter(({ position }) => !position))
    slots.push(item);
  for (const { item, position } of selected.filter(
    ({ position }) => position > 0,
  )) {
    let index = position - 1;
    while (slots[index] !== undefined) index++;
    slots[index] = item;
  }
  let index = 0;
  for (const item of api.slice(0, limit - selected.length)) {
    while (slots[index] !== undefined) index++;
    slots[index++] = item;
  }
  return slots.filter((item) => item !== undefined);
}

/** Reveal reserved manual slots only after preceding API slots have loaded. */
function blendProgressiveReviews(manual, api, exhausted = false) {
  // This is a paginated feed, not the capped featured carousel. Keep every
  // loaded API card and reserve manual positions across the entire feed.
  const ordered = blendReviewItems(manual, api, manual.length + api.length);
  if (exhausted) return ordered;
  const ready = [];
  for (const item of ordered) {
    const entry = manual.find((entry) => entry.item === item);
    if (entry) {
      if (entry.position > ready.length + 1) break;
    }
    ready.push(item);
  }
  return ready;
}

class FtsProductReviews extends HTMLElement {
  #abortController = new AbortController();
  #featuredAbortController;
  #featuredTimeout;
  #currentPage = 0;
  #lastPage = Number.POSITIVE_INFINITY;
  #reviewIds = new Set();
  #featuredReviewIds = new Set();
  #manualFeaturedCards = [];
  #mixedApiCards = [];
  #mediaUrls = new Set();
  #galleryMedia = [];
  #galleryEntries = [];
  #vimeoMedia = new Map();
  #isLoading = false;
  #observer;
  #masonryObserver;
  #masonryFrame;
  #lastFocusedMediaButton;
  #lightboxMedia = [];
  #lightboxIndex = 0;
  #lightboxRenderId = 0;

  #writeReviewObserver;
  #writeReviewFocusTimer;

  #setupWriteReview() {
    const button = this.querySelector("[data-write-review]");
    const widget = this.querySelector("[data-write-review-widget]");
    if (!button || !widget) return;
    const sync = () => {
      const nativeButton = Array.from(
        widget.querySelectorAll(".jdgm-write-rev-link"),
      ).at(-1);
      const expanded = nativeButton?.getAttribute("aria-expanded") === "true";
      const available =
        nativeButton &&
        nativeButton.closest(".jdgm--done-setup-widget") &&
        nativeButton.style.display !== "none";
      if (expanded && button.getAttribute("aria-expanded") !== "true") {
        clearTimeout(this.#writeReviewFocusTimer);
        this.#writeReviewFocusTimer = setTimeout(() => {
          if (
            !this.isConnected ||
            button.getAttribute("aria-expanded") !== "true"
          )
            return;
          const titleInput = Array.from(
            widget.querySelectorAll(
              '.jdgm-form-wrapper input[name="review_title"]',
            ),
          ).find(
            (input) =>
              !input.disabled &&
              input.getClientRects().length > 0 &&
              getComputedStyle(input).visibility === "visible",
          );
          titleInput?.focus({ preventScroll: true });
          widget.scrollIntoView({ block: "start", behavior: "smooth" });
        }, 500);
      } else if (!expanded && button.getAttribute("aria-expanded") === "true") {
        clearTimeout(this.#writeReviewFocusTimer);
        button.focus({ preventScroll: true });
      }
      button.hidden = !available;
      button.setAttribute("aria-expanded", String(expanded));
      button.textContent = expanded ? "Cancel review" : "Write a review";
    };
    button.addEventListener(
      "click",
      () => {
        Array.from(widget.querySelectorAll(".jdgm-write-rev-link"))
          .at(-1)
          ?.click();
        sync();
      },
      { signal: this.#abortController.signal },
    );
    this.#writeReviewObserver = new MutationObserver(sync);
    this.#writeReviewObserver.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class", "aria-expanded"],
    });
    sync();
  }

  connectedCallback() {
    this.#setupWriteReview();
    this.#loadMoreButton?.addEventListener("click", this.#handleLoadMore, {
      signal: this.#abortController.signal,
    });
    this.#retryButton?.addEventListener("click", this.#handleRetry, {
      signal: this.#abortController.signal,
    });
    this.#lightboxCloseButton?.addEventListener("click", this.#closeLightbox, {
      signal: this.#abortController.signal,
    });
    this.#lightboxPreviousButton?.addEventListener(
      "click",
      this.#showPreviousLightboxMedia,
      { signal: this.#abortController.signal },
    );
    this.#lightboxNextButton?.addEventListener(
      "click",
      this.#showNextLightboxMedia,
      { signal: this.#abortController.signal },
    );
    this.#lightbox?.addEventListener("click", this.#handleLightboxBackdrop, {
      signal: this.#abortController.signal,
    });

    this.#initializeServerRenderedGallery();
    this.#applyEmbeddedDistribution();
    this.#initializeServerRenderedFeaturedCards();

    if (this.#mixedFeed && this.#token && Number(this.dataset.reviewCount) > 0)
      this.#renderMixedFeed();

    if (!this.#token || Number(this.dataset.reviewCount) === 0) return;

    if (
      this.dataset.designMode === "true" ||
      !("IntersectionObserver" in window)
    ) {
      this.#loadInitialContent();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#loadInitialContent();
      },
      { rootMargin: "400px 0px" },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#writeReviewObserver?.disconnect();
    clearTimeout(this.#writeReviewFocusTimer);
    this.#observer?.disconnect();
    this.#masonryObserver?.disconnect();
    if (this.#masonryFrame) cancelAnimationFrame(this.#masonryFrame);
    if (this.#featuredTimeout) clearTimeout(this.#featuredTimeout);
    this.#featuredAbortController?.abort();
    this.#abortController.abort();
  }

  get #token() {
    return this.dataset.apiToken?.trim() || "";
  }

  get #loadMoreButton() {
    return this.querySelector("[data-load-more]");
  }

  get #retryButton() {
    return this.querySelector("[data-retry]");
  }

  get #status() {
    return this.querySelector("[data-status]");
  }

  get #reviewList() {
    return this.querySelector("[data-review-list]");
  }

  get #featuredSection() {
    return this.querySelector("[data-featured-section]");
  }

  get #featuredList() {
    return this.querySelector("[data-featured-list]");
  }

  get #mediaSection() {
    return this.querySelector("[data-media-section]");
  }

  get #mediaList() {
    return this.querySelector("[data-media-list]");
  }

  get #lightbox() {
    return this.querySelector("[data-lightbox]");
  }

  get #lightboxContent() {
    return this.querySelector("[data-lightbox-content]");
  }

  get #lightboxCloseButton() {
    return this.querySelector("[data-lightbox-close]");
  }

  get #lightboxPreviousButton() {
    return this.querySelector("[data-lightbox-previous]");
  }

  get #lightboxNextButton() {
    return this.querySelector("[data-lightbox-next]");
  }

  get #lightboxPosition() {
    return this.querySelector("[data-lightbox-position]");
  }

  #handleLoadMore = () => this.#loadPage(this.#currentPage + 1);

  #handleRetry = () => this.#loadPage(Math.max(1, this.#currentPage + 1));

  #handleLightboxBackdrop = (event) => {
    if (event.target === this.#lightbox) this.#closeLightbox();
  };

  #closeLightbox = () => {
    this.#lightboxRenderId += 1;
    this.#lightbox?.close();
    this.#lightboxContent?.replaceChildren();
    this.#lightboxMedia = [];
    this.#lightboxIndex = 0;
    this.#updateLightboxControls();
    this.#lastFocusedMediaButton?.focus();
  };

  #showPreviousLightboxMedia = () => {
    if (this.#lightboxIndex === 0) return;
    this.#lightboxIndex -= 1;
    this.#renderLightboxMedia();
  };

  #showNextLightboxMedia = () => {
    if (this.#lightboxIndex >= this.#lightboxMedia.length - 1) return;
    this.#lightboxIndex += 1;
    this.#renderLightboxMedia();
  };

  #loadInitialContent() {
    this.#loadPage(1);
    if (
      !this.#mixedFeed &&
      this.#featuredEnabled &&
      this.#remainingFeaturedLimit > 0
    )
      this.#loadFeaturedReviews();
  }

  async #loadFeaturedReviews() {
    if (!this.#featuredList || !this.#featuredSection) return;
    const remainingLimit = this.#remainingFeaturedLimit;
    if (remainingLimit === 0) return;

    this.#featuredAbortController = new AbortController();
    this.#featuredTimeout = setTimeout(
      () => this.#featuredAbortController?.abort(),
      FEATURED_TIMEOUT,
    );

    try {
      const html = await this.#fetchFeaturedCarousel(
        this.#featuredAbortController.signal,
      );
      const parsedDocument = new DOMParser().parseFromString(html, "text/html");
      const reviewIds = this.#parseFeaturedReviewIds(parsedDocument);
      const reviews = await this.#resolveFeaturedReviews(
        reviewIds,
        this.#featuredAbortController.signal,
        remainingLimit,
      );
      if (!reviews.length) return;

      const fragment = document.createDocumentFragment();
      for (const review of reviews) {
        this.#featuredReviewIds.add(review.id);
        this.#reviewIds.add(review.id);
        this.#reviewList
          ?.querySelector(`[data-review-id="${CSS.escape(review.id)}"]`)
          ?.remove();
        fragment.append(this.#renderFeaturedReview(review));
      }

      this.#featuredList.append(fragment);
      this.#orderFeaturedCards();
      this.#featuredSection.hidden = false;
      this.#setupMasonry();
    } catch (error) {
      if (error.name !== "AbortError")
        this.#featuredSection.hidden =
          this.#featuredList.childElementCount === 0;
    } finally {
      if (this.#featuredTimeout) clearTimeout(this.#featuredTimeout);
      this.#featuredTimeout = undefined;
    }
  }

  async #fetchFeaturedCarousel(signal) {
    const url = new URL(FEATURED_API_URL);
    url.searchParams.set("shop_domain", this.dataset.shopDomain || "");
    url.searchParams.set("api_token", this.#token);
    return this.#requestWidget(url, signal);
  }

  #parseFeaturedReviewIds(document) {
    const ids = [];
    const seen = new Set();
    for (const element of document.querySelectorAll(
      ".jdgm-carousel-item[data-review-id]",
    )) {
      const id = element.dataset.reviewId?.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
      if (ids.length >= FEATURED_CANDIDATE_LIMIT) break;
    }
    return ids;
  }

  async #resolveFeaturedReviews(reviewIds, signal, limit) {
    const results = new Array(reviewIds.length);
    let nextIndex = 0;
    let matchCount = 0;

    const worker = async () => {
      while (
        nextIndex < reviewIds.length &&
        matchCount < limit &&
        !signal.aborted
      ) {
        const index = nextIndex;
        nextIndex += 1;

        try {
          const review = await this.#fetchFeaturedReview(
            reviewIds[index],
            signal,
          );
          if (!review) continue;
          results[index] = review;
          matchCount += 1;
        } catch (error) {
          if (error.name === "AbortError") return;
        }
      }
    };

    await Promise.all(
      Array.from(
        { length: Math.min(FEATURED_CONCURRENCY, reviewIds.length) },
        () => worker(),
      ),
    );

    return results.filter(Boolean).slice(0, limit);
  }

  async #fetchFeaturedReview(reviewId, signal) {
    const url = new URL(REVIEW_DETAIL_API_URL);
    url.searchParams.set("shop_domain", this.dataset.shopDomain || "");
    url.searchParams.set("platform", "shopify");
    url.searchParams.set("api_token", this.#token);
    url.searchParams.set("product_id", this.dataset.productId || "");
    url.searchParams.set("review_uuid", reviewId);
    url.searchParams.set("page", "1");
    url.searchParams.set("per_page", "1");

    const html = await this.#requestWidget(url, signal);
    const document = new DOMParser().parseFromString(html, "text/html");
    const element = [...document.querySelectorAll(".jdgm-rev")].find(
      (reviewElement) => reviewElement.dataset.reviewId === reviewId,
    );
    if (!element || !this.#matchesCurrentProduct(element)) return null;

    return { ...this.#normalizeReview(element), featured: true };
  }

  #matchesCurrentProduct(element) {
    const sourceUrl = element.dataset.productUrl;
    const productUrl = this.dataset.productUrl;
    if (!sourceUrl || !productUrl) return false;

    try {
      const sourcePath = new URL(sourceUrl, window.location.origin).pathname
        .replace(/\/$/, "")
        .toLowerCase();
      const productPath = new URL(productUrl, window.location.origin).pathname
        .replace(/\/$/, "")
        .toLowerCase();
      return sourcePath === productPath;
    } catch (_error) {
      return false;
    }
  }

  async #loadPage(page) {
    if (this.#isLoading || page > this.#lastPage) return;

    this.#isLoading = true;
    this.#setLoadingState(true);

    try {
      const html = await this.#fetchWidget(page);
      const document = new DOMParser().parseFromString(html, "text/html");
      const parsed = this.#parseWidget(document, page);
      const newReviews = parsed.reviews.filter((review) => {
        if (
          this.#featuredReviewIds.has(review.id) ||
          this.#reviewIds.has(review.id)
        )
          return false;
        this.#reviewIds.add(review.id);
        return true;
      });

      for (const review of newReviews) {
        if (this.#mixedFeed)
          this.#mixedApiCards.push(this.#renderFeaturedReview(review));
        else this.#reviewList?.append(this.#renderReview(review));
      }

      this.#appendGalleryMedia([
        ...parsed.galleryMedia,
        ...newReviews.flatMap((review) => review.media),
      ]);
      if (parsed.distribution) this.#applyDistribution(parsed.distribution);

      this.#currentPage = page;
      this.#lastPage = parsed.lastPage;
      const isFinalPage =
        page >= parsed.lastPage || parsed.reviews.length < this.#perPage;
      if (this.#mixedFeed) this.#renderMixedFeed(isFinalPage);

      if (this.#status) {
        this.#status.textContent = this.#reviewIds.size
          ? ""
          : "No reviews yet.";
      }
      if (this.#loadMoreButton) this.#loadMoreButton.hidden = isFinalPage;
      if (this.#retryButton) this.#retryButton.hidden = true;
    } catch (error) {
      if (error.name === "AbortError") return;
      if (this.#mixedFeed && this.#currentPage === 0)
        this.#renderMixedFeed(true);
      if (this.#status)
        this.#status.textContent =
          "Reviews could not be loaded. Please try again.";
      if (this.#retryButton) this.#retryButton.hidden = false;
    } finally {
      this.#isLoading = false;
      this.#setLoadingState(false);
    }
  }

  async #fetchWidget(page) {
    const url = new URL(API_URL);
    url.searchParams.set("shop_domain", this.dataset.shopDomain || "");
    url.searchParams.set("external_id", this.dataset.productId || "");
    // Query authentication avoids the unsupported CORS preflight caused by a custom auth header.
    url.searchParams.set("api_token", this.#token);
    url.searchParams.set("page", String(page));
    url.searchParams.set("per_page", String(this.#perPage));
    return this.#requestWidget(url);
  }

  async #requestWidget(url, signal = this.#abortController.signal) {
    const response = await fetch(url, {
      signal,
    });

    if (!response.ok)
      throw new Error(`Judge.me request failed with ${response.status}`);

    const body = await response.text();
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("json")) return body;

    const payload = JSON.parse(body);
    const html = [
      payload.featured_carousel,
      payload.widget,
      payload.html,
      payload.reviews,
      payload.reviews_html,
      payload.data?.widget,
      payload.data?.html,
      payload.data?.reviews,
      payload.data,
    ].find((value) => typeof value === "string" && value.includes("<"));
    if (typeof html !== "string")
      throw new Error("Judge.me response did not include widget HTML");
    return html;
  }

  #parseWidget(document, requestedPage) {
    const reviews = [...document.querySelectorAll(".jdgm-rev")].map((element) =>
      this.#normalizeReview(element),
    );

    const paginationPages = [
      ...document.querySelectorAll(".jdgm-paginate__page"),
    ]
      .map((element) =>
        Number(element.dataset.page || element.textContent.trim()),
      )
      .filter(Number.isFinite);

    return {
      reviews,
      lastPage: paginationPages.length
        ? Math.max(...paginationPages)
        : Math.max(
            requestedPage,
            Math.ceil(Number(this.dataset.reviewCount) / this.#perPage),
          ),
      distribution: this.#parseDistribution(document),
      galleryMedia: this.#parseGalleryMedia(document),
    };
  }

  #normalizeReview(element) {
    const ratingElement = element.querySelector(".jdgm-rev__rating");
    const rating = Number(
      ratingElement?.dataset.score ||
        ratingElement?.getAttribute("data-rating") ||
        ratingElement?.querySelectorAll(".jdgm-star.jdgm--on").length ||
        0,
    );
    const authorElement = element.querySelector(".jdgm-rev__author");
    const timestamp = element.querySelector(".jdgm-rev__timestamp");
    const avatarElement = element.querySelector(
      ".jdgm-rev__icon img, .jm-reviewer-avatar img, img.jdgm-rev__avatar",
    );
    const media = this.#parseMedia(element);
    const body = this.#text(element, ".jdgm-rev__body");
    const title = this.#text(element, ".jdgm-rev__title");
    const author = authorElement?.textContent.trim() || "Anonymous";
    const id =
      element.dataset.reviewId ||
      `${author}-${timestamp?.textContent.trim() || ""}-${rating}-${title}-${body}`;
    const verified =
      element.dataset.verifiedBuyer === "true" ||
      authorElement?.dataset.verifiedBuyer === "true" ||
      Boolean(
        element.querySelector(".jdgm-rev__buyer-badge, .jdgm-rev__icon-buyer"),
      );
    return {
      id,
      rating: Math.min(5, Math.max(0, rating)),
      author,
      verified,
      featured: false,
      initials: this.#initials(author),
      avatarUrl: this.#safeMediaUrl(
        avatarElement?.currentSrc ||
          avatarElement?.src ||
          avatarElement?.dataset.src ||
          avatarElement?.getAttribute("src"),
      ),
      title,
      body,
      createdAt:
        timestamp?.getAttribute("datetime") ||
        timestamp?.dataset.content ||
        timestamp?.textContent.trim() ||
        "",
      media,
    };
  }

  #parseMedia(root) {
    const media = [];
    const seen = new Set();
    const selectors = [
      ".jdgm-rev__pic-link img",
      ".jdgm-rev__video-link video",
      ".jdgm-rev__video-link source",
      ".jdgm-rev__pics img",
      ".jdgm-rev__video video",
    ];

    for (const element of root.querySelectorAll(selectors.join(","))) {
      const rawUrl =
        element.currentSrc ||
        element.src ||
        element.dataset.src ||
        element.getAttribute("src");
      const url = this.#safeMediaUrl(rawUrl);
      if (!url || seen.has(url)) continue;
      seen.add(url);
      const isVideo =
        element.matches("video, source") || /\.(mp4|webm|mov)(\?|$)/i.test(url);
      const videoElement = element.matches("video")
        ? element
        : element.closest("video");
      media.push({
        type: isVideo ? "video" : "image",
        url,
        poster: isVideo
          ? this.#safeMediaUrl(
              videoElement?.poster || videoElement?.getAttribute("poster"),
            )
          : null,
      });
    }

    for (const element of root.querySelectorAll(
      ".jdgm-vid-player[data-external-id]",
    )) {
      const video = this.#externalVideoMedia(element.dataset.externalId);
      this.#addGalleryMedia(media, seen, video);
    }

    return media;
  }

  #parseGalleryMedia(document) {
    const media = [];
    const seen = new Set();

    for (const element of document.querySelectorAll(
      ".jdgm-gallery-data[data-json]",
    )) {
      let reviews;
      try {
        reviews = JSON.parse(element.dataset.json || "[]");
      } catch (_error) {
        continue;
      }

      if (!Array.isArray(reviews)) continue;

      for (const review of reviews) {
        for (const picture of review.pictures_urls || []) {
          const rawUrl =
            typeof picture === "string"
              ? picture
              : picture.original || picture.compact;
          this.#addGalleryMedia(media, seen, {
            type: "image",
            url: this.#safeMediaUrl(rawUrl),
          });
        }

        for (const externalId of review.video_external_ids || []) {
          this.#addGalleryMedia(
            media,
            seen,
            this.#externalVideoMedia(externalId),
          );
        }

        for (const video of review.media_platform_hosted_video_infos || []) {
          if (
            video.media_platform_name === "yt" &&
            /^[\w-]+$/.test(video.media_platform_url || "")
          ) {
            this.#addGalleryMedia(media, seen, {
              type: "embed",
              url: `https://www.youtube.com/embed/${video.media_platform_url}`,
              poster: `https://img.youtube.com/vi/${video.media_platform_url}/sddefault.jpg`,
            });
            continue;
          }

          const rawUrl =
            video.video_url || video.playback_url || video.url || video.src;
          const rawPoster =
            video.thumbnail_url || video.poster_url || video.poster;
          this.#addGalleryMedia(media, seen, {
            type: "video",
            url: this.#safeMediaUrl(rawUrl),
            poster: this.#safeMediaUrl(rawPoster),
          });
        }
      }
    }

    return media;
  }

  #externalVideoMedia(externalId) {
    const id = String(externalId || "");
    if (!/^[\w-]+$/.test(id)) return {};

    // Judge.me stores Bunny Stream videos as UUIDs; older Cloudflare IDs are hex strings.
    if (/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id)) {
      return {
        type: "embed",
        url: `https://iframe.mediadelivery.net/embed/748386/${id}?preload=false`,
        poster: `https://review-videos.judgeme.com/${id}/thumbnail.jpg`,
      };
    }

    if (id.length > 16) {
      return {
        type: "embed",
        url: `https://iframe.videodelivery.net/${id}`,
        poster: `https://cloudflarestream.com/${id}/thumbnails/thumb_5_0.jpg`,
      };
    }

    return {
      type: "vimeo",
      url: `https://vimeo.com/${id}`,
      externalId: id,
    };
  }

  #addGalleryMedia(media, seen, item) {
    if (!item.url || seen.has(item.url)) return;
    seen.add(item.url);
    media.push(item);
  }

  #parseDistribution(document) {
    const totals = {};
    for (const row of document.querySelectorAll(
      ".jdgm-histogram__row[data-rating]",
    )) {
      const rating = Number(row.dataset.rating);
      const count = Number(
        row.dataset.frequency ||
          row
            .querySelector(".jdgm-histogram__frequency")
            ?.textContent.replace(/[^0-9]/g, "") ||
          0,
      );
      if (rating >= 1 && rating <= 5) totals[rating] = count;
    }
    return Object.keys(totals).length ? totals : null;
  }

  #renderReview(review) {
    const article = document.createElement("article");
    article.className = "fts-product-reviews__review";
    article.dataset.reviewId = review.id;

    const header = document.createElement("div");
    header.className = "fts-product-reviews__review-header";

    const authorLine = document.createElement("div");
    authorLine.className = "fts-product-reviews__review-author-line";
    const author = document.createElement("strong");
    author.textContent = review.author;
    authorLine.append(author);

    if (review.verified) {
      const verified = document.createElement("span");
      verified.className = "fts-product-reviews__verified";
      verified.textContent = "Verified Customer";
      authorLine.append(verified);
    }

    const time = this.#createReviewTime(review.createdAt);
    header.append(authorLine);
    if (time) header.append(time);
    article.append(header, this.#createStars(review.rating));

    if (review.title) {
      const title = document.createElement("h3");
      title.className = "fts-product-reviews__review-title";
      title.textContent = review.title;
      article.append(title);
    }

    if (review.body) {
      const body = document.createElement("p");
      body.className = "fts-product-reviews__review-body";
      body.textContent = review.body;
      article.append(body);
    }

    if (review.media.length) {
      const mediaList = document.createElement("div");
      mediaList.className = "fts-product-reviews__review-media";
      for (const [index, media] of review.media.entries()) {
        mediaList.append(
          this.#createMediaButton(
            media,
            "fts-product-reviews__review-media-button",
            review.media,
            index,
          ),
        );
      }
      article.append(mediaList);
    }

    return article;
  }

  #renderFeaturedReview(review) {
    const article = document.createElement("article");
    article.className = "fts-product-reviews__featured-card";
    article.dataset.reviewId = review.id;
    article.setAttribute("role", "listitem");

    const content = document.createElement("div");
    content.className = "fts-product-reviews__featured-card-inner";

    const header = document.createElement("div");
    header.className = "fts-product-reviews__featured-header";

    const avatar = document.createElement("span");
    avatar.className = "fts-product-reviews__featured-avatar";
    avatar.setAttribute("aria-hidden", "true");
    if (review.avatarUrl) {
      const image = document.createElement("img");
      image.src = review.avatarUrl;
      image.alt = "";
      image.loading = "lazy";
      avatar.append(image);
    } else {
      avatar.textContent = review.initials;
    }

    const identity = document.createElement("div");
    identity.className = "fts-product-reviews__featured-identity";
    const author = document.createElement("strong");
    author.textContent = review.author;
    identity.append(author);

    if (review.verified) {
      const verified = document.createElement("span");
      verified.className = "fts-product-reviews__verified";
      verified.textContent = "Verified Customer";
      identity.append(verified);
    }

    header.append(avatar, identity);

    const ratingMeta = document.createElement("div");
    ratingMeta.className = "fts-product-reviews__featured-rating-meta";
    ratingMeta.append(this.#createStars(review.rating));
    const time = this.#createReviewTime(review.createdAt);
    if (time) ratingMeta.append(time);
    content.append(header, ratingMeta);

    if (review.title) {
      const title = document.createElement("h4");
      title.className = "fts-product-reviews__featured-title";
      title.textContent = review.title;
      content.append(title);
    }

    if (review.body) {
      const body = document.createElement("p");
      body.className = "fts-product-reviews__featured-body";
      body.id = `${this.id || "fts-product-reviews"}-featured-body-${review.id}`;
      body.textContent = review.body;

      const toggle = document.createElement("button");
      toggle.dataset.featuredExpand = "";
      toggle.className = "fts-product-reviews__featured-expand";
      toggle.type = "button";
      toggle.hidden = true;
      toggle.textContent = "Read more";
      toggle.setAttribute("aria-controls", body.id);
      toggle.setAttribute("aria-expanded", "false");

      content.append(body, toggle);
      this.#initializeFeaturedBody(body, toggle);
    }

    if (review.media.length) {
      const mediaList = document.createElement("div");
      mediaList.className = "fts-product-reviews__featured-media";
      const visibleMedia = review.media.slice(0, 2);
      for (const [index, media] of visibleMedia.entries()) {
        const button = this.#createMediaButton(
          media,
          "fts-product-reviews__featured-media-button",
          review.media,
          index,
        );
        const hiddenMediaCount = review.media.length - visibleMedia.length;
        if (index === visibleMedia.length - 1 && hiddenMediaCount > 0) {
          const overflow = document.createElement("span");
          overflow.className = "fts-product-reviews__featured-media-overflow";
          overflow.textContent = `+${hiddenMediaCount}`;
          button.append(overflow);
        }
        mediaList.append(button);
      }
      content.append(mediaList);
    }

    article.append(content);
    return article;
  }

  #initializeServerRenderedGallery() {
    if (!this.#mediaList || !this.#mediaSection) return;

    const buttons = [
      ...this.#mediaList.querySelectorAll("[data-manual-gallery-media]"),
    ];
    buttons.sort(
      (a, b) => Number(a.dataset.manualOrder) - Number(b.dataset.manualOrder),
    );
    for (const button of buttons) {
      const type = button.dataset.mediaType === "video" ? "video" : "image";
      const url =
        type === "video"
          ? this.#safeShopifyVideoUrl(button.dataset.mediaUrl)
          : this.#safeMediaUrl(button.dataset.mediaUrl);
      const poster = this.#safeMediaUrl(button.dataset.mediaPoster);

      if (
        !url ||
        this.#mediaUrls.has(url) ||
        this.#galleryMedia.length >= this.#mediaLimit
      ) {
        button.hidden = true;
        continue;
      }

      const media = { type, url, poster };
      this.#mediaUrls.add(url);
      this.#galleryMedia.push(media);
      this.#galleryEntries.push({
        media,
        button,
        manual: true,
        position: Number(button.dataset.position) || 0,
      });
      if (type === "video") this.#watchVideoPoster(button.querySelector("img"));
      button.addEventListener(
        "click",
        () =>
          this.#openLightboxGallery(
            this.#galleryMedia,
            this.#galleryMedia.indexOf(media),
            button,
          ),
        { signal: this.#abortController.signal },
      );
    }

    this.#orderGalleryMedia();
    this.#mediaSection.hidden = this.#galleryMedia.length === 0;
  }

  #initializeServerRenderedFeaturedCards() {
    if (!this.#featuredList || !this.#featuredSection) return;

    this.#manualFeaturedCards = [
      ...this.#featuredList.querySelectorAll("[data-manual-featured-review]"),
    ];
    for (const card of this.#manualFeaturedCards) {
      this.#initializeFeaturedBody(
        card.querySelector("[data-featured-body]"),
        card.querySelector("[data-featured-expand]"),
      );

      const mediaButtons = [
        ...card.querySelectorAll("[data-manual-featured-media]"),
      ];
      const mediaEntries = mediaButtons
        .map((button) => ({
          button,
          media: {
            type: "image",
            url: this.#safeMediaUrl(button.dataset.mediaUrl),
          },
        }))
        .filter(({ media }) => media.url);
      const galleryMedia = mediaEntries.map(({ media }) => media);

      for (const [index, { button }] of mediaEntries.entries()) {
        button.addEventListener(
          "click",
          () => this.#openLightboxGallery(galleryMedia, index, button),
          { signal: this.#abortController.signal },
        );
      }
    }

    this.#orderFeaturedCards();
    const hasFeaturedCards = this.#featuredList.childElementCount > 0;
    this.#featuredSection.hidden = !hasFeaturedCards;
    if (hasFeaturedCards) this.#setupMasonry();
  }

  #orderFeaturedCards() {
    if (!this.#featuredList) return;
    const api = [...this.#featuredList.children].filter(
      (card) => !card.hasAttribute("data-manual-featured-review"),
    );
    this.#featuredList.append(
      ...blendReviewItems(
        this.#manualFeaturedCards.map((item) => ({
          item,
          position: Number(item.dataset.position) || 0,
        })),
        api,
        this.#featuredLimit,
      ),
    );
  }

  get #mixedFeed() {
    return this.dataset.mixManualReviewsIntoFeed === "true";
  }

  #renderMixedFeed(exhausted = false) {
    if (!this.#featuredList || !this.#featuredSection) return;
    const cards = blendProgressiveReviews(
      this.#manualFeaturedCards.map((item) => ({
        item,
        position: Number(item.dataset.position) || 0,
      })),
      this.#mixedApiCards,
      exhausted,
    );
    this.#featuredList.replaceChildren(...cards);
    this.#featuredSection.hidden = cards.length === 0;
    requestAnimationFrame(() => {
      for (const card of cards) {
        const body = card.querySelector(".fts-product-reviews__featured-body");
        const toggle = card.querySelector("[data-featured-expand]");
        if (!card.isConnected || !body || !toggle) continue;
        toggle.hidden =
          !body.classList.contains("is-expanded") &&
          body.scrollHeight <= body.clientHeight + 1;
      }
    });
    if (cards.length) this.#setupMasonry();
  }

  #initializeFeaturedBody(body, toggle) {
    if (!body || !toggle || toggle.dataset.initialized === "true") return;
    toggle.dataset.initialized = "true";
    toggle.addEventListener(
      "click",
      () => {
        const expanded = body.classList.toggle("is-expanded");
        toggle.textContent = expanded ? "Read less" : "Read more";
        toggle.setAttribute("aria-expanded", String(expanded));
        this.#scheduleMasonryLayout();
      },
      { signal: this.#abortController.signal },
    );

    requestAnimationFrame(() => {
      toggle.hidden = body.scrollHeight <= body.clientHeight + 1;
    });
  }

  #createReviewTime(value) {
    if (!value) return null;

    const normalizedValue = value
      .replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) UTC$/, "$1T$2Z")
      .trim();
    const date = new Date(normalizedValue);
    const time = document.createElement("time");
    time.className = "fts-product-reviews__review-meta";

    if (Number.isNaN(date.getTime())) {
      time.textContent = value;
      return time;
    }

    time.dateTime = date.toISOString();
    time.textContent = this.#formatRelativeDate(date);
    return time;
  }

  #formatRelativeDate(date) {
    const seconds = (date.getTime() - Date.now()) / 1000;
    const absoluteSeconds = Math.abs(seconds);
    const units = [
      ["year", 31557600],
      ["month", 2629800],
      ["week", 604800],
      ["day", 86400],
      ["hour", 3600],
      ["minute", 60],
      ["second", 1],
    ];
    const [unit, unitSeconds] =
      units.find(([, threshold]) => absoluteSeconds >= threshold) ||
      units[units.length - 1];

    try {
      return new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(
        Math.round(seconds / unitSeconds),
        unit,
      );
    } catch (_error) {
      return date.toLocaleDateString();
    }
  }

  #createStars(rating) {
    const stars = document.createElement("span");
    stars.className = "fts-product-reviews__stars";
    stars.setAttribute("aria-label", `${rating} out of 5 stars`);
    for (let index = 1; index <= 5; index += 1) {
      const star = document.createElement("span");
      star.className = `fts-product-reviews__star${rating >= index ? " is-filled" : ""}`;
      star.setAttribute("aria-hidden", "true");
      star.textContent = "★";
      stars.append(star);
    }
    return stars;
  }

  #setupMasonry() {
    if (!this.#featuredList) return;
    this.#masonryObserver?.disconnect();

    if ("ResizeObserver" in window) {
      this.#masonryObserver = new ResizeObserver(() =>
        this.#scheduleMasonryLayout(),
      );
      this.#masonryObserver.observe(this.#featuredList);
      for (const content of this.#featuredList.querySelectorAll(
        ".fts-product-reviews__featured-card-inner",
      )) {
        this.#masonryObserver.observe(content);
      }
    }

    this.#scheduleMasonryLayout();
  }

  #scheduleMasonryLayout() {
    if (this.#masonryFrame) cancelAnimationFrame(this.#masonryFrame);
    this.#masonryFrame = requestAnimationFrame(() => {
      this.#masonryFrame = undefined;
      this.#layoutMasonry();
    });
  }

  #layoutMasonry() {
    if (!this.#featuredList) return;
    const styles = getComputedStyle(this.#featuredList);
    const columnCount = styles.gridTemplateColumns.split(" ").length;
    const rowHeight = Number.parseFloat(styles.gridAutoRows) || 4;
    const rowGap = Number.parseFloat(styles.rowGap) || 0;

    for (const card of this.#featuredList.children) {
      if (columnCount === 1) {
        card.style.gridRowEnd = "auto";
        continue;
      }

      const content = card.querySelector(
        ".fts-product-reviews__featured-card-inner",
      );
      if (!content) continue;
      const height = content.getBoundingClientRect().height;
      const span = Math.ceil((height + rowGap) / (rowHeight + rowGap));
      card.style.gridRowEnd = `span ${span}`;
    }
  }

  #appendGalleryMedia(mediaItems) {
    if (!this.#mediaList || !this.#mediaSection) return;
    let galleryChanged = false;

    for (const media of mediaItems) {
      if (this.#galleryMedia.length >= this.#mediaLimit) break;
      const url = this.#safeMediaUrl(media.url);
      if (!url || this.#mediaUrls.has(url)) continue;

      const normalizedMedia = { ...media, url };
      const index = this.#galleryMedia.length;
      this.#mediaUrls.add(url);
      this.#galleryMedia.push(normalizedMedia);
      galleryChanged = true;
      const button = this.#createMediaButton(
        normalizedMedia,
        "fts-product-reviews__media-button",
        this.#galleryMedia,
        index,
      );
      this.#galleryEntries.push({
        media: normalizedMedia,
        button,
        manual: false,
        position: 0,
      });
    }

    if (galleryChanged) this.#orderGalleryMedia();
    this.#mediaSection.hidden = this.#galleryMedia.length === 0;
  }

  #orderGalleryMedia() {
    const activeMedia =
      this.#lightboxMedia === this.#galleryMedia
        ? this.#galleryMedia[this.#lightboxIndex]
        : null;
    const ordered = blendReviewItems(
      this.#galleryEntries
        .filter((entry) => entry.manual)
        .map((item) => ({ item, position: item.position })),
      this.#galleryEntries.filter((entry) => !entry.manual),
      this.#mediaLimit,
    );
    this.#galleryMedia.splice(
      0,
      this.#galleryMedia.length,
      ...ordered.map((entry) => entry.media),
    );
    this.#mediaList.append(...ordered.map((entry) => entry.button));
    if (activeMedia) {
      this.#lightboxIndex = this.#galleryMedia.indexOf(activeMedia);
      if (this.#lightbox?.open) this.#updateLightboxControls();
    }
  }

  #createMediaButton(
    media,
    className,
    galleryMedia = [media],
    galleryIndex = 0,
  ) {
    const button = document.createElement("button");
    button.className = className;
    button.dataset.pdpReviewMedia = media.type === "image" ? "image" : "video";
    button.type = "button";
    button.setAttribute(
      "aria-label",
      `Open customer ${media.type === "image" ? "image" : "video"}`,
    );
    const thumbnail = this.#createMediaElement(media, true);
    button.append(thumbnail);
    if (media.type !== "image" && media.poster)
      button.append(this.#createVideoBadge());
    if (media.type === "vimeo")
      this.#hydrateVimeoThumbnail(thumbnail, button, media);
    button.addEventListener(
      "click",
      () =>
        this.#openLightboxGallery(
          galleryMedia,
          galleryMedia.indexOf(media) >= 0
            ? galleryMedia.indexOf(media)
            : galleryIndex,
          button,
        ),
      { signal: this.#abortController.signal },
    );
    return button;
  }

  #createMediaElement(media, thumbnail = false) {
    if (media.type === "embed" || media.type === "vimeo") {
      if (thumbnail) {
        if (media.poster) {
          return this.#createVideoPoster(media.poster);
        }
        return this.#createVideoPlaceholder("Play video", true);
      }

      const iframe = document.createElement("iframe");
      iframe.src = media.url;
      iframe.title = "Customer review video";
      iframe.allow = "autoplay; fullscreen; picture-in-picture";
      iframe.allowFullscreen = true;
      return iframe;
    }

    if (media.type === "video") {
      if (thumbnail) {
        if (media.poster) {
          return this.#createVideoPoster(media.poster);
        }
        return this.#createVideoPlaceholder("Play video", true);
      }

      const video = document.createElement("video");
      video.src = media.url;
      if (media.poster) video.poster = media.poster;
      video.playsInline = true;
      video.preload = "auto";
      video.controls = true;
      return video;
    }

    const image = document.createElement("img");
    image.src = media.url;
    image.alt = "Customer review photo";
    image.loading = thumbnail ? "lazy" : "eager";
    return image;
  }

  #createVideoPoster(url) {
    const image = document.createElement("img");
    image.alt = "Customer review video";
    image.loading = "lazy";
    this.#watchVideoPoster(image);
    image.src = url;
    return image;
  }

  #watchVideoPoster(image) {
    if (!image || image.dataset.posterFallbackInitialized === "true") return;
    image.dataset.posterFallbackInitialized = "true";

    const showFallback = () => {
      if (!image.parentNode) return;
      image.removeAttribute("alt");
      image.removeAttribute("src");
      image.removeAttribute("srcset");
      const badge = [...image.parentNode.children].find((child) =>
        child.classList.contains("fts-product-reviews__video-badge"),
      );
      badge?.remove();
      image.replaceWith(this.#createVideoPlaceholder("Play video", true));
      this.#scheduleMasonryLayout();
    };

    image.addEventListener("error", showFallback, { once: true });
    if (image.complete && image.naturalWidth === 0)
      queueMicrotask(() => {
        if (image.complete && image.naturalWidth === 0) showFallback();
      });
  }

  async #hydrateVimeoThumbnail(thumbnail, button, media) {
    try {
      const resolvedMedia = await this.#resolveVimeoMedia(media);
      if (!thumbnail.isConnected || !resolvedMedia.poster) return;
      thumbnail.replaceWith(this.#createMediaElement(resolvedMedia, true));
      button.append(this.#createVideoBadge());
      this.#scheduleMasonryLayout();
    } catch (_error) {
      return;
    }
  }

  #openLightboxGallery(mediaItems, index, trigger) {
    if (!this.#lightbox || !this.#lightboxContent || !mediaItems.length) return;
    this.#lastFocusedMediaButton = trigger;
    this.#lightboxMedia = mediaItems;
    this.#lightboxIndex = Math.min(
      Math.max(0, index),
      this.#lightboxMedia.length - 1,
    );
    if (!this.#lightbox.open) this.#lightbox.showModal();
    this.#renderLightboxMedia();
  }

  async #renderLightboxMedia() {
    const media = this.#lightboxMedia[this.#lightboxIndex];
    if (!media || !this.#lightboxContent) return;
    const renderId = ++this.#lightboxRenderId;
    this.#lightboxContent.replaceChildren(
      this.#createVideoPlaceholder("Loading…"),
    );
    this.#updateLightboxControls();

    try {
      const resolvedMedia =
        media.type === "vimeo" ? await this.#resolveVimeoMedia(media) : media;
      if (renderId !== this.#lightboxRenderId || !this.#lightbox?.open) return;
      this.#lightboxContent.replaceChildren(
        this.#createMediaElement(resolvedMedia),
      );
    } catch (error) {
      if (
        error.name === "AbortError" ||
        renderId !== this.#lightboxRenderId ||
        !this.#lightbox?.open
      )
        return;
      this.#lightboxContent.replaceChildren(
        this.#createVideoPlaceholder("Video unavailable"),
      );
    }
  }

  #updateLightboxControls() {
    const hasGallery = this.#lightboxMedia.length > 1;
    if (this.#lightboxPreviousButton) {
      this.#lightboxPreviousButton.hidden = !hasGallery;
      this.#lightboxPreviousButton.disabled = this.#lightboxIndex === 0;
    }
    if (this.#lightboxNextButton) {
      this.#lightboxNextButton.hidden = !hasGallery;
      this.#lightboxNextButton.disabled =
        this.#lightboxIndex >= this.#lightboxMedia.length - 1;
    }
    if (this.#lightboxPosition) {
      this.#lightboxPosition.hidden = !hasGallery;
      this.#lightboxPosition.textContent = hasGallery
        ? `${this.#lightboxIndex + 1} of ${this.#lightboxMedia.length}`
        : "";
    }
  }

  async #resolveVimeoMedia(media) {
    const cached = this.#vimeoMedia.get(media.externalId);
    if (cached) return cached;

    const url = new URL("https://vimeo.com/api/oembed.json");
    url.searchParams.set("url", media.url);
    url.searchParams.set("title", "false");
    const response = await fetch(url, { signal: this.#abortController.signal });
    if (!response.ok)
      throw new Error(`Vimeo request failed with ${response.status}`);

    const payload = await response.json();
    const oembedDocument = new DOMParser().parseFromString(
      payload.html || "",
      "text/html",
    );
    const playerUrl = this.#safeMediaUrl(
      oembedDocument.querySelector("iframe")?.getAttribute("src"),
    );
    if (!playerUrl || new URL(playerUrl).hostname !== "player.vimeo.com") {
      throw new Error("Vimeo response did not include a player URL");
    }

    const resolved = {
      type: "embed",
      url: playerUrl,
      poster: this.#safeMediaUrl(payload.thumbnail_url),
    };
    this.#vimeoMedia.set(media.externalId, resolved);
    return resolved;
  }

  #createVideoBadge() {
    const badge = document.createElement("span");
    badge.className = "fts-product-reviews__video-badge";
    badge.setAttribute("aria-hidden", "true");
    badge.textContent = "▶";
    return badge;
  }

  #createVideoPlaceholder(text, showPlayIcon = false) {
    const placeholder = document.createElement("span");
    placeholder.className = "fts-product-reviews__video-placeholder";
    if (showPlayIcon) {
      placeholder.setAttribute("aria-hidden", "true");
      const icon = document.createElement("span");
      icon.className = "fts-product-reviews__play-icon";
      icon.textContent = "▶";
      placeholder.append(icon);
    } else {
      placeholder.textContent = text;
    }
    return placeholder;
  }

  #applyEmbeddedDistribution() {
    const script = this.querySelector("[data-review-totals]");
    if (!script?.textContent.trim()) return;

    try {
      const parsed = JSON.parse(script.textContent);
      const distribution = this.#normalizeDistribution(parsed);
      if (!distribution) return;

      const expectedTotal = Number(this.dataset.reviewCount);
      const distributionTotal = Object.values(distribution).reduce(
        (sum, count) => sum + Number(count || 0),
        0,
      );
      if (expectedTotal > 0 && distributionTotal !== expectedTotal) {
        const distributionElement = this.querySelector(
          ".fts-product-reviews__distribution",
        );
        if (distributionElement) distributionElement.hidden = true;
        return;
      }

      this.#applyDistribution(distribution);
    } catch (_error) {
      // The fetched widget histogram remains the fallback for undocumented metafield shapes.
    }
  }

  #normalizeDistribution(value) {
    if (!value) return null;
    if (value.review_totals)
      return this.#normalizeDistribution(value.review_totals);
    if (value.totals) return this.#normalizeDistribution(value.totals);

    const distribution = {};
    if (Array.isArray(value)) {
      for (const [index, item] of value.entries()) {
        if (typeof item === "object") {
          const rating = Number(item.rating || item.score || index + 1);
          distribution[rating] = this.#extractCount(item);
        } else {
          distribution[index + 1] = this.#extractCount(item);
        }
      }
    } else if (typeof value === "object") {
      const words = { one: 1, two: 2, three: 3, four: 4, five: 5 };
      for (const [key, item] of Object.entries(value)) {
        const numericMatch = key.match(/[1-5]/);
        const wordMatch = Object.entries(words).find(([word]) =>
          key.toLowerCase().includes(word),
        );
        const rating = Number(numericMatch?.[0] || wordMatch?.[1]);
        if (rating >= 1 && rating <= 5)
          distribution[rating] = this.#extractCount(item);
      }
    }

    return Object.keys(distribution).length ? distribution : null;
  }

  #extractCount(value) {
    if (typeof value === "number") return value;
    if (typeof value === "string")
      return Number(value.replace(/[^0-9]/g, "")) || 0;
    return Number(value?.count || value?.frequency || value?.total || 0);
  }

  #applyDistribution(distribution) {
    const total = Object.values(distribution).reduce(
      (sum, count) => sum + Number(count || 0),
      0,
    );
    const distributionElement = this.querySelector(
      ".fts-product-reviews__distribution",
    );
    if (distributionElement) distributionElement.hidden = false;

    for (let rating = 1; rating <= 5; rating += 1) {
      const row = this.querySelector(`[data-rating-row="${rating}"]`);
      if (!row) continue;
      const count = Number(distribution[rating] || 0);
      const percentage = total ? (count / total) * 100 : 0;
      const fill = row.querySelector("[data-rating-fill]");
      const countElement = row.querySelector("[data-rating-count]");
      if (fill) fill.style.width = `${percentage}%`;
      if (countElement) countElement.textContent = String(count);
    }
  }

  #setLoadingState(isLoading) {
    this.setAttribute("aria-busy", String(isLoading));
    if (this.#loadMoreButton) this.#loadMoreButton.disabled = isLoading;
    if (this.#retryButton) this.#retryButton.disabled = isLoading;
    if (isLoading && this.#status)
      this.#status.textContent = "Loading reviews…";
  }

  #safeMediaUrl(value) {
    return safeMediaUrl(value);
  }

  #safeShopifyVideoUrl(value) {
    const url = this.#safeMediaUrl(value);
    if (!url) return null;

    const hostname = new URL(url).hostname.toLowerCase();
    const shopDomain = (this.dataset.shopDomain || "").toLowerCase();
    const isShopifyHost =
      hostname === shopDomain ||
      hostname === window.location.hostname.toLowerCase() ||
      hostname.endsWith(".myshopify.com") ||
      hostname === "cdn.shopify.com" ||
      hostname.endsWith(".shopifycdn.com");
    return isShopifyHost ? url : null;
  }

  #text(root, selector) {
    return root.querySelector(selector)?.textContent.trim() || "";
  }

  #initials(author) {
    const words = author.trim().split(/\s+/).filter(Boolean);
    return (
      words
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase() || "A"
    );
  }

  get #perPage() {
    return Math.max(1, Number(this.dataset.reviewsPerPage) || 5);
  }

  get #mediaLimit() {
    return Math.min(50, Math.max(1, Number(this.dataset.mediaLimit) || 6));
  }

  get #featuredEnabled() {
    return this.dataset.featuredEnabled === "true";
  }

  get #featuredLimit() {
    return Math.min(50, Math.max(1, Number(this.dataset.featuredLimit) || 6));
  }

  get #remainingFeaturedLimit() {
    return Math.max(
      0,
      this.#featuredLimit - (this.#featuredList?.childElementCount || 0),
    );
  }
}

if (!customElements.get("fts-product-reviews")) {
  customElements.define("fts-product-reviews", FtsProductReviews);
}
